// Super Admin — Inquiry Management (CRM)
import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, Mail, Phone, MessageSquare, Search, Filter as FilterIcon,
  Download, Plus, Loader2, Flame, Calendar, X,
} from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { RowActionsMenu } from "@/components/admin/RowActionsMenu";
import { StatusFilterTabs } from "@/components/admin/StatusFilterTabs";
import { useLifecycleActions } from "@/components/admin/useLifecycleActions";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError, API_BASE } from "@/lib/api";

const STATUSES = ["new", "contacted", "qualified", "converted", "closed", "lost", "not_interested", "duplicate", "spam"];
const INQUIRY_TYPES = ["course_inquiry", "hospital_lms_inquiry", "training_program_inquiry", "partnership_inquiry", "demo_request", "callback_request", "general_inquiry", "other"];
const SOURCES = ["website", "lms", "manual", "phone_call", "whatsapp", "email", "referral", "social_media", "hospital_visit", "facebook_lead_form", "facebook_ads", "whatsapp_business", "whatsapp_manual", "imported_csv", "other"];
const PRIORITIES = ["low", "medium", "high", "urgent"];
const FLAGS = ["hot_lead", "important", "follow_up_due", "payment_expected", "demo_required", "proposal_required", "callback_required", "not_interested", "duplicate", "spam"];
const LEAD_STAGES = ["fresh_lead", "first_call_pending", "follow_up_required", "demo_scheduled", "proposal_shared", "payment_pending", "converted", "not_interested", "closed"];

const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-700",
  contacted: "bg-amber-100 text-amber-700",
  qualified: "bg-purple-100 text-purple-700",
  converted: "bg-emerald-100 text-emerald-700",
  closed: "bg-slate-100 text-slate-500",
  lost: "bg-rose-100 text-rose-700",
  not_interested: "bg-slate-100 text-slate-500",
  duplicate: "bg-slate-100 text-slate-500",
  spam: "bg-rose-100 text-rose-700",
};
const PRIORITY_COLORS = {
  low: "bg-slate-100 text-slate-600",
  medium: "bg-blue-50 text-blue-700",
  high: "bg-amber-50 text-amber-700",
  urgent: "bg-rose-50 text-rose-700",
};
const FLAG_COLORS = {
  hot_lead: "bg-orange-100 text-orange-700",
  important: "bg-purple-100 text-purple-700",
  follow_up_due: "bg-amber-100 text-amber-700",
  payment_expected: "bg-emerald-100 text-emerald-700",
  demo_required: "bg-indigo-100 text-indigo-700",
  proposal_required: "bg-blue-100 text-blue-700",
  callback_required: "bg-amber-100 text-amber-700",
  not_interested: "bg-slate-100 text-slate-500",
  duplicate: "bg-slate-100 text-slate-500",
  spam: "bg-rose-100 text-rose-700",
};

const title = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const EMPTY_MANUAL = {
  inquiry_type: "general_inquiry", source: "manual",
  full_name: "", email: "", phone: "", alternate_mobile: "",
  organization_name: "", designation: "", city: "", state: "",
  course_interested: "", service_interested: "", message: "",
  priority: "medium", status: "new", lead_stage: "fresh_lead",
  flag: "", assigned_to: "", next_followup_date: "", next_followup_time: "",
  remarks: "", internal_notes_summary: "",
};

const EMPTY_FILTERS = {
  status_filter: "active",
  status: "all", source: "all", inquiry_type: "all",
  priority: "all", flag: "all", assigned_to: "all",
  course: "", city: "", state: "",
  followup_due_today: false, followup_overdue: false,
  created_from: "", created_to: "",
};

export function AdminInquiriesPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [advOpen, setAdvOpen] = useState(false);

  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState(EMPTY_MANUAL);
  const [manualSaving, setManualSaving] = useState(false);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const params = { page, page_size: 25, ...filters, search: search || undefined };
      // strip "all" & falsy
      Object.keys(params).forEach((k) => {
        if (params[k] === "all" || params[k] === "" || params[k] === false || params[k] == null) delete params[k];
      });
      const { data } = await api.get("/admin/inquiries", { params });
      setRows(data.data.inquiries || []);
      setTotal(data.data.total || 0);
      const s = await api.get("/admin/inquiries/summary");
      setSummary(s.data.data);
    } catch (err) { toast.error(formatApiError(err)); setRows([]); }
  }, [page, filters, search]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/inquiries/assignable-users");
        setUsers(data.data.users || []);
      } catch { /* silent */ }
    })();
  }, []);

  const lc = useLifecycleActions({
    basePath: "/admin/inquiries",
    labelOf: (row) => `"${row.full_name}"`,
    onDone: load,
  });

  const saveManual = async () => {
    if (!manualForm.full_name || !manualForm.phone) return toast.error("Name and Phone are required.");
    setManualSaving(true);
    try {
      const body = { ...manualForm };
      // Empty strings → null for optional selects/dates
      if (!body.email) delete body.email;
      if (!body.assigned_to) delete body.assigned_to;
      if (!body.flag) delete body.flag;
      if (!body.next_followup_date) delete body.next_followup_date;
      if (!body.next_followup_time) delete body.next_followup_time;
      await api.post("/admin/inquiries", body);
      toast.success("Inquiry added successfully.");
      setManualOpen(false);
      setManualForm(EMPTY_MANUAL);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setManualSaving(false); }
  };

  const downloadExcel = async () => {
    try {
      const params = { ...filters, search: search || undefined };
      Object.keys(params).forEach((k) => {
        if (params[k] === "all" || params[k] === "" || params[k] === false || params[k] == null) delete params[k];
      });
      const qs = new URLSearchParams(params).toString();
      const url = `${API_BASE}/admin/inquiries/export/excel${qs ? `?${qs}` : ""}`;
      const resp = await fetch(url, { credentials: "include" });
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const dl = document.createElement("a");
      const href = URL.createObjectURL(blob);
      dl.href = href;
      const yyyymmdd = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      dl.download = `inquiry_report_${yyyymmdd}.xlsx`;
      document.body.appendChild(dl); dl.click();
      dl.remove(); URL.revokeObjectURL(href);
      toast.success("Inquiry report downloaded successfully.");
    } catch (err) { toast.error(err.message || "Export failed"); }
  };

  const clearFilters = () => { setFilters(EMPTY_FILTERS); setSearch(""); setPage(1); };

  const changeStatus = async (row, newStatus) => {
    try {
      await api.patch(`/admin/inquiries/${row.id}/status`, { status: newStatus });
      toast.success("Inquiry status updated successfully.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <DashboardLayout
      title="Inquiry Management"
      breadcrumbs={["Super Admin", "Inquiries"]}
    >
      <div className="mb-6 text-sm text-slate-600">
        Manage website, LMS, hospital, course, and manually added inquiries in one CRM dashboard.
      </div>

      {/* Dashboard cards */}
      {summary && (
        <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-5 lg:grid-cols-10" data-testid="inquiry-cards">
          <Kpi label="Total" value={summary.total} />
          <Kpi label="New" value={summary.new} tone="blue" />
          <Kpi label="Contacted" value={summary.contacted} tone="amber" />
          <Kpi label="Qualified" value={summary.qualified} tone="purple" />
          <Kpi label="Converted" value={summary.converted} tone="emerald" />
          <Kpi label="Closed" value={summary.closed} tone="slate" />
          <Kpi label="Lost" value={summary.lost} tone="rose" />
          <Kpi label="Hot Leads" value={summary.hot_leads} tone="orange" icon={<Flame className="h-3.5 w-3.5" />} />
          <Kpi label="Due Today" value={summary.followup_due_today} tone="amber"
            onClick={() => { setF("followup_due_today", true); setF("followup_overdue", false); }} />
          <Kpi label="Overdue" value={summary.followup_overdue} tone="rose"
            onClick={() => { setF("followup_overdue", true); setF("followup_due_today", false); }} />
        </div>
      )}

      {/* Toolbar */}
      <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <StatusFilterTabs value={filters.status_filter}
            onChange={(v) => setF("status_filter", v)} testId="inquiries-status-filter" />
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <button onClick={() => setAdvOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              data-testid="inquiry-advanced-filter-btn">
              <FilterIcon className="h-3.5 w-3.5" /> Advanced Filters
            </button>
            <button onClick={downloadExcel}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              data-testid="inquiry-export-btn">
              <Download className="h-3.5 w-3.5" /> Download Excel
            </button>
            <button onClick={() => setManualOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-[#101876] px-3 py-2 text-xs font-bold text-white hover:bg-[#0c135c]"
              data-testid="inquiry-add-btn">
              <Plus className="h-3.5 w-3.5" /> Add Inquiry
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[240px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder="Search name, email, phone, code, message…"
              value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              data-testid="inquiry-search" />
          </div>
          <QuickSelect value={filters.status} setter={(v) => setF("status", v)} label="Status"
            options={[{ v: "all", l: "All statuses" }, ...STATUSES.map((s) => ({ v: s, l: title(s) }))]} />
          <QuickSelect value={filters.source} setter={(v) => setF("source", v)} label="Source"
            options={[{ v: "all", l: "All sources" }, ...SOURCES.map((s) => ({ v: s, l: title(s) }))]} />
          <QuickSelect value={filters.priority} setter={(v) => setF("priority", v)} label="Priority"
            options={[{ v: "all", l: "All priorities" }, ...PRIORITIES.map((s) => ({ v: s, l: title(s) }))]} />
          <QuickSelect value={filters.flag} setter={(v) => setF("flag", v)} label="Flag"
            options={[{ v: "all", l: "All flags" }, ...FLAGS.map((s) => ({ v: s, l: title(s) }))]} />
          <QuickSelect value={filters.assigned_to} setter={(v) => setF("assigned_to", v)} label="Assigned"
            options={[
              { v: "all", l: "Anyone" },
              { v: "unassigned", l: "Unassigned" },
              ...users.map((u) => ({ v: u.id, l: `${u.full_name || u.email} · ${title(u.role_code)}` })),
            ]} />
          {(Object.values(filters).some((v) => v && v !== "all" && v !== "active") || search) && (
            <button onClick={clearFilters} className="text-xs font-semibold text-slate-500 hover:text-[#101876]">Clear</button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="rounded-2xl border border-slate-200 bg-white" data-testid="inquiry-list">
        {rows === null ? (
          <LoadingBlock label="Loading inquiries…" />
        ) : rows.length === 0 ? (
          <EmptyState title="No inquiries match" description="Adjust filters or add a manual inquiry." icon={MessageSquare} />
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map((r) => (
              <div key={r.id} className="p-4 hover:bg-slate-50/60" data-testid={`inquiry-${r.id}`}>
                <div className="flex flex-wrap items-start gap-3">
                  <div className="min-w-[180px] flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link to={`/app/super-admin/inquiries/${r.id}`}
                        className="text-sm font-bold text-slate-900 hover:text-[#101876]">
                        {r.full_name}
                      </Link>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_COLORS[r.status] || "bg-slate-100 text-slate-500"}`}>
                        {title(r.status)}
                      </span>
                      {r.flag && (
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${FLAG_COLORS[r.flag] || "bg-slate-100"}`}>
                          {title(r.flag)}
                        </span>
                      )}
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${PRIORITY_COLORS[r.priority] || "bg-slate-100"}`}>
                        {title(r.priority)}
                      </span>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-600">
                        {title(r.source)}
                      </span>
                      <span className="rounded-full bg-slate-50 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500">
                        {title(r.inquiry_type)}
                      </span>
                    </div>
                    <div className="mt-1 font-mono text-[10px] text-slate-400">{r.inquiry_code}</div>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                      {r.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {r.email}</span>}
                      {r.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {r.phone}</span>}
                      {r.organization_name && <span>{r.organization_name}</span>}
                      {r.city && <span>{r.city}{r.state ? `, ${r.state}` : ""}</span>}
                    </div>
                    {r.message && <div className="mt-2 line-clamp-2 text-xs text-slate-600">{r.message}</div>}
                  </div>
                  <div className="text-right text-[10px] text-slate-500">
                    <div>Created {new Date(r.created_at).toLocaleDateString()}</div>
                    {r.next_followup_date && (
                      <div className="mt-1 inline-flex items-center gap-1 text-amber-700">
                        <Calendar className="h-3 w-3" /> {r.next_followup_date}{r.next_followup_time ? ` ${r.next_followup_time}` : ""}
                      </div>
                    )}
                    <div className="mt-1">
                      {r.assigned_to_user ? (
                        <span className="text-slate-600">→ {r.assigned_to_user.full_name || r.assigned_to_user.email}</span>
                      ) : (
                        <span className="text-slate-400">Unassigned</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Select value={r.status} onValueChange={(v) => changeStatus(r, v)}>
                      <SelectTrigger className="h-8 w-32 text-xs" data-testid={`inquiry-status-${r.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => <SelectItem key={s} value={s}>{title(s)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <RowActionsMenu
                      testId={`inquiry-actions-${r.id}`}
                      status={r.is_deleted ? "deleted" : "active"}
                      onView={() => nav(`/app/super-admin/inquiries/${r.id}`)}
                      onEdit={() => nav(`/app/super-admin/inquiries/${r.id}`)}
                      onDelete={() => lc.open("delete", r)}
                      onRestore={() => lc.open("restore", r)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {total > 25 && (
          <div className="flex items-center justify-between border-t border-slate-100 p-3 text-xs text-slate-500">
            <div>{total} results · page {page}</div>
            <div className="flex gap-2">
              <button disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded border border-slate-200 px-3 py-1 disabled:opacity-40">Prev</button>
              <button disabled={page * 25 >= total} onClick={() => setPage((p) => p + 1)}
                className="rounded border border-slate-200 px-3 py-1 disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Manual Add Inquiry dialog */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="inquiry-manual-form">
          <DialogHeader><DialogTitle>Add Inquiry</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <FieldSel label="Inquiry Type" v={manualForm.inquiry_type} on={(v) => setManualForm({ ...manualForm, inquiry_type: v })}
              opts={INQUIRY_TYPES.map((s) => ({ v: s, l: title(s) }))} tid="manual-type" />
            <FieldSel label="Source" v={manualForm.source} on={(v) => setManualForm({ ...manualForm, source: v })}
              opts={SOURCES.map((s) => ({ v: s, l: title(s) }))} tid="manual-source" />
            <FieldIn label="Name *" v={manualForm.full_name} on={(v) => setManualForm({ ...manualForm, full_name: v })} tid="manual-name" />
            <FieldIn label="Email" v={manualForm.email} on={(v) => setManualForm({ ...manualForm, email: v })} tid="manual-email" />
            <FieldIn label="Mobile *" v={manualForm.phone} on={(v) => setManualForm({ ...manualForm, phone: v })} tid="manual-phone" />
            <FieldIn label="Alternate Mobile" v={manualForm.alternate_mobile} on={(v) => setManualForm({ ...manualForm, alternate_mobile: v })} />
            <FieldIn label="Organization / Hospital" v={manualForm.organization_name} on={(v) => setManualForm({ ...manualForm, organization_name: v })} />
            <FieldIn label="Designation" v={manualForm.designation} on={(v) => setManualForm({ ...manualForm, designation: v })} />
            <FieldIn label="City" v={manualForm.city} on={(v) => setManualForm({ ...manualForm, city: v })} />
            <FieldIn label="State" v={manualForm.state} on={(v) => setManualForm({ ...manualForm, state: v })} />
            <FieldIn label="Course Interested" v={manualForm.course_interested} on={(v) => setManualForm({ ...manualForm, course_interested: v })} />
            <FieldIn label="Service Interested" v={manualForm.service_interested} on={(v) => setManualForm({ ...manualForm, service_interested: v })} />
            <div className="col-span-2 space-y-1">
              <Label>Message / Requirement</Label>
              <Textarea rows={3} value={manualForm.message} onChange={(e) => setManualForm({ ...manualForm, message: e.target.value })} />
            </div>
            <FieldSel label="Priority" v={manualForm.priority} on={(v) => setManualForm({ ...manualForm, priority: v })}
              opts={PRIORITIES.map((s) => ({ v: s, l: title(s) }))} />
            <FieldSel label="Status" v={manualForm.status} on={(v) => setManualForm({ ...manualForm, status: v })}
              opts={STATUSES.map((s) => ({ v: s, l: title(s) }))} />
            <FieldSel label="Lead Stage" v={manualForm.lead_stage} on={(v) => setManualForm({ ...manualForm, lead_stage: v })}
              opts={LEAD_STAGES.map((s) => ({ v: s, l: title(s) }))} />
            <FieldSel label="Flag" v={manualForm.flag || "none"} on={(v) => setManualForm({ ...manualForm, flag: v === "none" ? "" : v })}
              opts={[{ v: "none", l: "No flag" }, ...FLAGS.map((s) => ({ v: s, l: title(s) }))]} />
            <FieldSel label="Assigned To" v={manualForm.assigned_to || "unassigned"} on={(v) => setManualForm({ ...manualForm, assigned_to: v === "unassigned" ? "" : v })}
              opts={[{ v: "unassigned", l: "Unassigned" }, ...users.map((u) => ({ v: u.id, l: `${u.full_name || u.email} · ${title(u.role_code)}` }))]} />
            <FieldIn label="Next Follow-up Date" type="date" v={manualForm.next_followup_date} on={(v) => setManualForm({ ...manualForm, next_followup_date: v })} />
            <FieldIn label="Next Follow-up Time" type="time" v={manualForm.next_followup_time} on={(v) => setManualForm({ ...manualForm, next_followup_time: v })} />
            <div className="col-span-2 space-y-1">
              <Label>Remarks</Label>
              <Textarea rows={2} value={manualForm.remarks} onChange={(e) => setManualForm({ ...manualForm, remarks: e.target.value })} />
            </div>
            <div className="col-span-2 space-y-1">
              <Label>Internal Notes Summary</Label>
              <Textarea rows={2} value={manualForm.internal_notes_summary} onChange={(e) => setManualForm({ ...manualForm, internal_notes_summary: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setManualOpen(false)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={saveManual} disabled={manualSaving}
              className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70"
              data-testid="manual-save-btn">
              {manualSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save Inquiry
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Advanced Filter dialog */}
      <Dialog open={advOpen} onOpenChange={setAdvOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="inquiry-adv-filter">
          <DialogHeader><DialogTitle>Advanced Filters</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <FieldSel label="Inquiry Type" v={filters.inquiry_type} on={(v) => setF("inquiry_type", v)}
              opts={[{ v: "all", l: "All" }, ...INQUIRY_TYPES.map((s) => ({ v: s, l: title(s) }))]} />
            <FieldIn label="Course keyword" v={filters.course} on={(v) => setF("course", v)} />
            <FieldIn label="City" v={filters.city} on={(v) => setF("city", v)} />
            <FieldIn label="State" v={filters.state} on={(v) => setF("state", v)} />
            <FieldIn label="Created From" type="date" v={filters.created_from} on={(v) => setF("created_from", v)} />
            <FieldIn label="Created To" type="date" v={filters.created_to} on={(v) => setF("created_to", v)} />
            <label className="flex items-center gap-2 text-xs col-span-2">
              <input type="checkbox" checked={!!filters.followup_due_today}
                onChange={(e) => setF("followup_due_today", e.target.checked)} />
              Only follow-ups due today
            </label>
            <label className="flex items-center gap-2 text-xs col-span-2">
              <input type="checkbox" checked={!!filters.followup_overdue}
                onChange={(e) => setF("followup_overdue", e.target.checked)} />
              Only overdue follow-ups
            </label>
          </div>
          <DialogFooter>
            <button onClick={clearFilters} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Reset</button>
            <button onClick={() => setAdvOpen(false)} className="rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c]">Apply</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {lc.dialog}
    </DashboardLayout>
  );
}

function Kpi({ label, value, tone = "slate", icon, onClick }) {
  const map = {
    slate: "text-slate-900",
    blue: "text-blue-700", amber: "text-amber-700", purple: "text-purple-700",
    emerald: "text-emerald-700", rose: "text-rose-700", orange: "text-orange-700",
  };
  return (
    <button type="button" onClick={onClick}
      className="rounded-xl border border-slate-200 bg-white p-3 text-left transition hover:border-[#101876]/40 disabled:cursor-default">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
        {icon && <span className={map[tone]}>{icon}</span>}
      </div>
      <div className={`mt-1 text-2xl font-black ${map[tone]}`}>{value ?? 0}</div>
    </button>
  );
}

function QuickSelect({ value, setter, label, options }) {
  return (
    <Select value={value} onValueChange={setter}>
      <SelectTrigger className="w-40 text-xs"><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent className="max-h-72">
        {options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function FieldIn({ label, v, on, type = "text", tid }) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <Input type={type} value={v || ""} onChange={(e) => on(e.target.value)} data-testid={tid} />
    </div>
  );
}
function FieldSel({ label, v, on, opts, tid }) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <Select value={v} onValueChange={on}>
        <SelectTrigger data-testid={tid}><SelectValue /></SelectTrigger>
        <SelectContent className="max-h-72">
          {opts.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
